import { ReadingsDataRawResponse } from "@inrange/building-manager-api-client";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { Formatters } from "./types";

// ECharts default color palette for generating tooltip markers
const ECHARTS_COLORS = [
  "#5470c6",
  "#91cc75",
  "#fac858",
  "#ee6666",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
  "#ea7ccc",
];

const ChartRaw = ({
  data,
  selectedEsysUuid,
  startDate,
  endDate,
  selectedSources,
  selectedReadings,
  formatters,
}: {
  data: ReadingsDataRawResponse;
  selectedEsysUuid: string | undefined;
  startDate: string;
  endDate: string;
  selectedSources: string[];
  selectedReadings: string[];
  formatters: Formatters;
}) => {
  // Process chart data for raw readings
  const chartOptions = useMemo(() => {
    const allSeries = data.series;
    if (allSeries.length === 0) return {};

    // Filter series by esysUuid
    let seriesToDisplay = selectedEsysUuid
      ? allSeries.filter((s) => s.esysUuid === selectedEsysUuid)
      : allSeries;

    // Filter series by selected readings (hardwareType|serialNumber|readingName)
    if (selectedReadings.length > 0) {
      const readingsSet = new Set(selectedReadings);
      seriesToDisplay = seriesToDisplay.filter((s) =>
        readingsSet.has(`${s.hardwareType}|${s.serialNumber}|${s.readingName}`)
      );
    }

    if (seriesToDisplay.length === 0) return {};

    // Check if we have any EPM data to determine if we need dual axes
    const hasEpmData = seriesToDisplay.some((s) => s.hardwareType === "epm");
    // Check if we have battery SOC/SOH data that should use percentage scale (0-100)
    const hasBatteryPercentageData = seriesToDisplay.some(
      (s) => s.readingName === "BATTERY_SOC" || s.readingName === "BATTERY_SOH"
    );
    const needsRightAxis = hasEpmData || hasBatteryPercentageData;

    // Define gap threshold (e.g., 2 hours in milliseconds)
    const GAP_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours

    // Pre-calculate timestamp bounds for efficient filtering
    const { startMs, endMs } =
      startDate && endDate
        ? formatters.dateRangeToTimestamps(startDate, endDate)
        : { startMs: 0, endMs: Infinity };

    // Pre-compute source filter set for O(1) lookup
    const sourceFilterSet =
      selectedSources.length > 0 ? new Set(selectedSources) : null;

    const seriesConfig = seriesToDisplay.map((s) => {
      const xData = s.data.x;
      const readingData = s.data.reading;
      const sourceData = s.data.source;
      const ingestionData = s.data.ingestionTimestamp;
      const dataLength = xData.length;

      // Pre-parse all timestamps once - this is the expensive operation
      // Using a typed array for timestamps gives better cache locality
      const timestamps = new Float64Array(dataLength);
      for (let i = 0; i < dataLength; i++) {
        timestamps[i] = new Date(xData[i]).getTime();
      }

      // Build output arrays - they will grow dynamically as needed
      const mappedData: Array<[number, number | null]> = [];
      const customSource: (string | undefined)[] = [];
      const customIngestionTimestamp: (string | undefined)[] = [];
      const customOriginalTimestamp: (string | undefined)[] = [];

      let prevTimestamp = -Infinity;
      let prevShiftedTimestamp = 0;

      // Single-pass processing: filter, gap detection, and mapping combined
      for (let i = 0; i < dataLength; i++) {
        const pointMs = timestamps[i];

        // Date range filter
        if (pointMs < startMs || pointMs > endMs) continue;

        const source = sourceData[i];
        const shouldInclude =
          sourceFilterSet === null ||
          (source !== undefined && sourceFilterSet.has(source));

        // Check for time gap with previous included point
        if (prevTimestamp !== -Infinity) {
          const timeDiff = pointMs - prevTimestamp;

          // If gap is larger than threshold, insert null point
          if (timeDiff > GAP_THRESHOLD_MS) {
            mappedData.push([prevShiftedTimestamp, null]);
            customSource.push(undefined);
            customIngestionTimestamp.push(undefined);
            customOriginalTimestamp.push(undefined);
          }
        }

        // Use pre-parsed timestamp for shift calculation
        const shiftedTimestamp = formatters.shiftToSiteTimezone(pointMs);

        mappedData.push([
          shiftedTimestamp,
          shouldInclude ? readingData[i] : null,
        ]);
        customSource.push(source);
        customIngestionTimestamp.push(ingestionData[i]);
        customOriginalTimestamp.push(xData[i]);

        prevTimestamp = pointMs;
        prevShiftedTimestamp = shiftedTimestamp;
      }

      // EPM data and battery SOC/SOH use the right Y-axis (index 1)
      const isEpm = s.hardwareType === "epm";
      const isBatteryPercentage =
        s.readingName === "BATTERY_SOC" || s.readingName === "BATTERY_SOH";
      const useRightAxis = isEpm || isBatteryPercentage;

      return {
        name: `${s.hardwareType} - ${s.serialNumber} - ${s.readingName}`,
        type: "line",
        data: mappedData,
        symbol: "circle",
        symbolSize: 3,
        lineStyle: { width: 2 },
        connectNulls: false, // Don't connect lines across null values (gaps)
        yAxisIndex: useRightAxis ? 1 : 0,
        custom: {
          source: customSource,
          ingestionTimestamp: customIngestionTimestamp,
          originalTimestamp: customOriginalTimestamp,
        },
      };
    });

    // Build minute-level index for tooltip: group data points by minute
    // across all series so the tooltip can show all readings in the same minute.
    // When a series has multiple points in the same minute, keep only the latest.
    type MinuteEntry = {
      seriesName: string;
      value: number;
      source: string | undefined;
      originalTimestamp: string;
      seriesIndex: number;
    };
    const minuteIndex = new Map<number, Map<number, MinuteEntry>>();
    seriesConfig.forEach((series, seriesIdx) => {
      (series.data as Array<[number, number | null]>).forEach((point, idx) => {
        if (point[1] === null) return;
        const originalTs = series.custom.originalTimestamp[idx];
        if (!originalTs) return;
        const tsMs = new Date(originalTs).getTime();
        const minuteKey = Math.floor(tsMs / 60000);
        if (!minuteIndex.has(minuteKey)) {
          minuteIndex.set(minuteKey, new Map());
        }
        const minuteMap = minuteIndex.get(minuteKey)!;
        const existing = minuteMap.get(seriesIdx);
        // Keep the latest data point per series within each minute
        if (
          !existing ||
          tsMs > new Date(existing.originalTimestamp).getTime()
        ) {
          minuteMap.set(seriesIdx, {
            seriesName: series.name,
            value: point[1],
            source: series.custom.source[idx],
            originalTimestamp: originalTs,
            seriesIndex: seriesIdx,
          });
        }
      });
    });

    // Calculate required top margin based on number of legend items
    // Estimate ~150px per legend item on average (including spacing)
    // Assume chart width of ~1000px, so ~6-7 items per row
    const legendItemsPerRow = 6;
    const legendRows = Math.ceil(seriesConfig.length / legendItemsPerRow);
    const legendRowHeight = 25; // pixels per row
    const legendPadding = 40; // padding above and below legend
    const calculatedTopMargin = Math.max(
      legendRows * legendRowHeight + legendPadding,
      80 // minimum margin
    );

    return {
      animation: false,
      toolbox: {
        right: "40px",
        bottom: "10px",
        feature: {
          dataZoom: {
            yAxisIndex: "none",
          },
        },
      },
      dataZoom: [
        {
          type: "inside",
          xAxisIndex: [0],
          start: 0,
          end: 100,
          zoomOnMouseWheel: false,
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
        },
        formatter: (params: any) => {
          if (!params || params.length === 0) return "";

          // Get the original timestamp from custom data to avoid double timezone shift
          const firstSeries = seriesConfig.find(
            (s) => s.name === params[0].seriesName
          );
          const originalTimestamp = firstSeries?.custom?.originalTimestamp
            ? firstSeries.custom.originalTimestamp[params[0].dataIndex]
            : null;
          if (!originalTimestamp) return "";

          // Look up all data points in the same minute across all series
          const minuteKey = Math.floor(
            new Date(originalTimestamp).getTime() / 60000
          );
          const minuteData = minuteIndex.get(minuteKey);
          if (!minuteData || minuteData.size === 0) return "";

          const dateLabel = formatters.toDateTimeString(originalTimestamp);
          let tooltip = `<strong>${dateLabel}</strong><br/>`;

          minuteData.forEach((item) => {
            const color =
              ECHARTS_COLORS[item.seriesIndex % ECHARTS_COLORS.length];
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;
            tooltip += `${marker} ${item.seriesName}: ${item.value.toFixed(3)}`;
            if (item.source) tooltip += ` (${item.source})`;
            tooltip += "<br/>";
          });

          return tooltip;
        },
      },
      legend: {
        data: seriesToDisplay.map(
          (s) => `${s.hardwareType} - ${s.serialNumber} - ${s.readingName}`
        ),
        top: 0,
        type: "plain",
      },
      grid: {
        left: "60px",
        right: needsRightAxis ? "80px" : "20px",
        bottom: "100px",
        top: `${calculatedTopMargin}px`,
        containLabel: false,
      },
      xAxis: {
        type: "time",
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: needsRightAxis
        ? [
            {
              type: "value",
              name: "Reading value",
              position: "left",
            },
            {
              type: "value",
              name: hasBatteryPercentageData
                ? "Percentage (%)"
                : "Reading value",
              position: "right",
              min: hasBatteryPercentageData ? 0 : undefined,
              max: hasBatteryPercentageData ? 100 : undefined,
            },
          ]
        : {
            type: "value",
            name: "Reading Value",
          },
      series: seriesConfig,
    };
  }, [
    startDate,
    endDate,
    data.series,
    selectedEsysUuid,
    selectedSources,
    selectedReadings,
    formatters,
  ]);

  if (Object.keys(chartOptions).length === 0) {
    return (
      <Row>
        <Col>
          <div className="alert alert-warning" role="alert">
            No data available for the selected date range.
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="mt-3">
      <Col>
        <h5>
          Raw Readings Chart
          {data.truncated && (
            <span className="text-muted">
              {" "}
              (response limited to {(data.dataPointsLimit / 1000).toFixed(0)}K
              most recent readings)
            </span>
          )}
        </h5>
        <ReactECharts
          key={`${selectedEsysUuid}-${selectedReadings.join(",")}`}
          option={chartOptions}
          style={{ height: "500px" }}
        />
      </Col>
    </Row>
  );
};

export default ChartRaw;
